import { useTranslation } from '@silospot/i18n/react';
import type { OnboardingState } from '@silospot/shared';
import { Lock } from 'lucide-react';
import type { ReactElement } from 'react';
import { PageHeader } from './kit';
import { SetupChecklist } from './setup-checklist';

function LockedZone({ message, className }: { message: string; className: string }): ReactElement {
  return (
    <div
      className={`grid place-items-center rounded-xl border border-border bg-surface ${className}`}
    >
      <div className="px-4 py-6 text-center">
        <span className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-xl bg-neutral-bg text-text-3">
          <Lock className="h-4 w-4" />
        </span>
        <p className="mx-auto max-w-[230px] text-[12.5px] text-text-3">{message}</p>
      </div>
    </div>
  );
}

/**
 * Dashboard shown while onboarding is incomplete (locked). The layout chrome
 * stays; every data zone is a locked empty state — never real or fake data. A
 * pinned SetupChecklist drives the user back into the wizard.
 */
export function LockedDashboard({
  resume,
  onResume,
}: {
  resume: OnboardingState['resume'];
  onResume: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');
  const items = [
    { label: t('onb.railAddSite'), done: !!resume },
    { label: t('onb.railTracker'), done: !!resume?.trackerVerified },
    { label: t('onb.railAds'), done: !!resume?.adsLinked },
  ];
  const msg = t('onb.lockedZone');
  return (
    <div>
      <PageHeader title={t('onb.lockedTitle')} />
      <div className="mb-4">
        <SetupChecklist items={items} actionLabel={t('onb.resume')} onAction={onResume} />
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {['a', 'b', 'c', 'd'].map((k) => (
          <LockedZone key={k} message={msg} className="h-28" />
        ))}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
        <LockedZone message={msg} className="h-72" />
        <LockedZone message={msg} className="h-72" />
      </div>
    </div>
  );
}
